import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { Icon } from "../components/icons";
import { SearchInput } from "../components/SearchInput";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  PageHeader,
  buttonStyles,
  cx,
} from "../components/ui";
import { relativeTime } from "../lib/format";
import { CATEGORIES, STATUSES, waitingOn } from "../lib/support";
import { useTicketCounts, useTickets } from "../lib/queries";
import type { SupportCategory, SupportStatus, SupportTicket } from "../lib/types";
import { CategoryChip, PriorityChip, StatusBadge } from "./support/TicketBits";

/** How many to fetch at a time, and how many more each "Show more". */
const PAGE = 25;

/*
 * The tabs, in the order somebody scans them: what is with us, what is with
 * them, then the archive. "All" leads because a customer with three tickets
 * should not have to choose a tab to see three tickets.
 *
 * Every filter here is served by the API rather than applied to the loaded
 * rows. The list is paged, and a filter over one page silently answers a
 * different question from the one asked — "no billing tickets" when there are
 * four, twenty rows further down.
 */
type Filter = "all" | SupportStatus;

/*
 * "All" first, then the four statuses in the order the lib lists them.
 *
 * The tab's own word is `list.all`; the rest read `status.<value>.label`,
 * which is the same key the badge on every row uses — so a tab and the rows
 * under it can never disagree about what a state is called.
 */
const FILTERS: Filter[] = ["all", ...STATUSES];

export function SupportPage() {
  const { t } = useTranslation("support");
  const { currentOrg } = useAuth();
  const canWrite = useCan("developer");

  const [filter, setFilter] = useState<Filter>("all");
  const [category, setCategory] = useState<SupportCategory | "">("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const counts = useTicketCounts(currentOrg?.id);
  const { data, isPending, isPlaceholderData, error } = useTickets(currentOrg?.id, {
    ...(filter === "all" ? {} : { status: filter }),
    ...(category ? { category } : {}),
    ...(q ? { q } : {}),
    limit,
  });

  const tickets = data?.data ?? [];
  const total = data?.total ?? 0;
  const loadFailed = Boolean(error && tickets.length === 0);

  // Whether the account has any tickets at all, which is a different question
  // from whether this filter matched — and the difference is what decides
  // between "no tickets yet" and "nothing matches".
  const accountEmpty = (counts.data?.total ?? 0) === 0 && tickets.length === 0;
  const filtering = Boolean(q || category || filter !== "all");
  const partial = total > tickets.length;

  // Any filter change returns to the first page: twenty-five of the old filter
  // is rarely the right window on the new one.
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setLimit(PAGE);
    };
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("list.title")}
        description={
          currentOrg
            ? t("list.description", { organization: currentOrg.name })
            : t("list.descriptionNoOrg")
        }
        action={
          canWrite ? (
            <Link to="/support/new" className={buttonStyles("primary")}>
              <Icon name="plus" className="size-3.5" />
              {t("list.create")}
            </Link>
          ) : undefined
        }
      />

      {error && tickets.length > 0 && (
        <Alert tone="warning">{t("list.refreshFailed")}</Alert>
      )}

      {!accountEmpty && (
        <div className="space-y-3">
          {/*
            Counts come from their own request, not from the loaded page. The
            tabs describe the account; the list below describes the page.
          */}
          <div className="flex flex-wrap gap-1.5">
            {FILTERS
              /*
               * Hide a tab only when the counts say it is genuinely empty. If
               * that request has not landed — or failed — every tab renders
               * without a number: filtering is the feature, and the count is
               * decoration on it. The first version hid the tabs whenever the
               * count was missing, which turned a failed side request into a
               * page with no filters on it.
               */
              .filter(
                (key) =>
                  key === "all" || key === filter || !counts.data || (counts.data[key] ?? 0) > 0,
              )
              .map((key) => {
                const active = filter === key;
                const count = key === "all" ? counts.data?.total : counts.data?.[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => reset(setFilter)(key)}
                    aria-pressed={active}
                    className={cx(
                      "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] transition-colors duration-200 ease-smooth pointer-coarse:min-h-11",
                      active
                        ? "border-accent/40 bg-accent/8 text-accent"
                        : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                    )}
                  >
                    {key === "all" ? t("list.all") : t(`status.${key}.label`)}
                    {count !== undefined && (
                      <span
                        className={cx("tabular-nums", active ? "text-accent/70" : "text-fg-subtle")}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={q}
              onChange={reset(setQ)}
              placeholder={t("list.search")}
              className="w-full sm:max-w-xs"
            />

            {/* A select rather than five more chips: the tabs above are the
                filter people use, and a second row of the same shape competes
                with it for the same glance. */}
            <label className="flex items-center gap-2 text-[13px] text-fg-subtle">
              {t("list.about")}
              <select
                value={category}
                onChange={(event) => reset(setCategory)(event.target.value as SupportCategory | "")}
                aria-label={t("list.aboutLabel")}
                className="h-8 rounded-md border border-line bg-canvas px-2 text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong focus:border-accent focus:outline-none pointer-coarse:min-h-11"
              >
                <option value="">{t("list.anything")}</option>
                {CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {t(`category.${value}.label`)}
                  </option>
                ))}
              </select>
            </label>

            {filtering && (
              <button
                type="button"
                onClick={() => {
                  setFilter("all");
                  setCategory("");
                  setQ("");
                  setLimit(PAGE);
                }}
                className="text-[13px] text-fg-subtle underline decoration-line-strong underline-offset-4 transition-colors hover:text-fg"
              >
                {t("list.clear")}
              </button>
            )}

            {!isPending && !error && (
              <p className="ml-auto text-[13px] text-fg-subtle">
                {t("list.count", { count: total })}
                {filtering && t("list.matching")}
              </p>
            )}
          </div>
        </div>
      )}

      <Card>
        {isPending ? (
          <LoadingBlock label={t("list.loading")} />
        ) : loadFailed ? (
          <div className="p-5">
            <Alert>{t("list.loadFailed")}</Alert>
          </div>
        ) : accountEmpty ? (
          <EmptyState
            icon="support"
            title={t("list.empty.title")}
            description={t("list.empty.description")}
            action={
              canWrite ? (
                <Link to="/support/new" className={buttonStyles("primary")}>
                  {t("list.empty.create")}
                </Link>
              ) : undefined
            }
          />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon="support"
            title={t("list.noMatch.title")}
            description={
              q ? t("list.noMatch.query", { query: q }) : t("list.noMatch.state")
            }
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFilter("all");
                  setCategory("");
                  setQ("");
                  setLimit(PAGE);
                }}
              >
                {t("list.clear")}
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {tickets.map((ticket) => (
              <TicketRow key={ticket.id} ticket={ticket} />
            ))}
          </ul>
        )}

        {/* The control apps and databases use. Not a numbered pager: nobody
            navigates to page four of their own support history — they load
            more until they see the thread they remember. */}
        {partial && (
          <div className="border-t border-line p-2.5">
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              loading={isPlaceholderData}
              onClick={() => setLimit((current) => current + PAGE)}
            >
              Show more ({total - tickets.length} older)
            </Button>
          </div>
        )}
      </Card>

      {/*
        No footer note. Each thing one said is now said where it is acted on:
        that a ticket reaches the people who run the platform is on the form
        that opens one, that anyone in the organization can reply is on the
        thread's own rail, and that a banner beats a ticket during an incident
        is in the guidance beside the composer. Nothing here promises a
        response time, which is the one thing this page must never do.
      */}
    </div>
  );
}

function TicketRow({ ticket }: { ticket: SupportTicket }) {
  const { t } = useTranslation("support");
  const turn = waitingOn(ticket);

  return (
    <li>
      <Link
        to={`/support/${ticket.id}`}
        className="group flex items-start gap-4 px-5 py-4 transition-colors duration-200 ease-smooth hover:bg-fg/4"
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="ident text-[11px] text-fg-subtle">{ticket.reference}</span>
            <StatusBadge status={ticket.status} />
            <PriorityChip priority={ticket.priority} />
          </div>

          <p className="truncate text-[13px] font-medium text-fg group-hover:text-accent">
            {ticket.subject}
          </p>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-fg-subtle">
            <CategoryChip category={ticket.category} />
            {(ticket.appName || ticket.databaseName) && (
              <span className="inline-flex items-center gap-1">
                <Icon name={ticket.appName ? "apps" : "database"} className="size-3 text-fg-subtle" />
                {ticket.appName ?? ticket.databaseName}
              </span>
            )}
            <span aria-hidden="true">·</span>
            {/* Whose words were last, which is what a returning customer is
                actually checking. The badge says the same thing formally; this
                says it in the language of a conversation. */}
            <span>
              {t(ticket.lastMessageRole === "staff" ? "weReplied" : "youWrote", {
                at: relativeTime(ticket.lastMessageAt),
              })}
            </span>
            {ticket.messageCount > 1 && (
              <>
                <span aria-hidden="true">·</span>
                <span>{ticket.messageCount} messages</span>
              </>
            )}
          </div>
        </div>

        <div className="hidden shrink-0 items-center gap-2 pt-1 sm:flex">
          {turn === "you" && <span className="text-[12px] text-warn">{t("list.yourTurn")}</span>}
          <Icon
            name="chevron"
            className="size-3.5 text-fg-subtle transition-colors group-hover:text-accent"
          />
        </div>
      </Link>
    </li>
  );
}
